/* ═══════════════════════════════════════════════════════════════════
   ProductDetailPage — deep product view surfaced when a brand clicks
   any SKU card in the Portfolio grid.

   Sections:
     • Hero: image, name, status pill, launch/plan CTA, velocity
     • Performance stat rail: Revenue / ROAS / Velocity / Impressions / Success
     • AI Insight callout
     • Positioning + target audience + top channels + competitor set
     • Ingredient highlights
     • Proven Wave case study with proof points
     • Wave calendar (existing data)
   ═══════════════════════════════════════════════════════════════════ */
import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import {
  ArrowLeft, Zap, TrendingUp, Target, Eye, DollarSign, Award, Users,
  Sparkles, FlaskConical, Calendar, Check, BarChart3, Package,
} from 'lucide-react';
import {
  PORTFOLIO_SKUS, SKU_DETAIL, STATUS_COLORS,
} from '../components/portfolio/skuCatalog';
import { useDemoProfile } from '../hooks/useDemoProfile';

const T = '#00F5FF';
const P = '#FF6BBD';
const BG = '#0A0A0A';
const CARD = '#121212';
const BORDER = '#1E1E1E';

function StatTile({ label, value, color = T, icon: Icon }) {
  return (
    <div className="rounded-xl p-4 text-center" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
      <Icon className="h-4 w-4 mx-auto mb-1.5" style={{ color }} />
      <p className="text-[8px] uppercase tracking-widest text-gray-500 font-bold mb-1">{label}</p>
      <p className="text-xl font-black tabular-nums" style={{ color }}>{value}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children, color = T }) {
  return (
    <section className="rounded-xl p-5" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4" style={{ color }} />
        <h2 className="text-xs font-black text-white uppercase tracking-widest">{title}</h2>
      </div>
      {children}
    </section>
  );
}

export default function ProductDetailPage() {
  const { skuId } = useParams();
  const { profile, isDemo } = useDemoProfile();

  // Demo brands (Helix, Grandburger, ShineWash) ship their own SKU lists
  // via /api/demo/profile. Look there FIRST so clicking a Helix car or a
  // Grandburger menu item lands on a page populated with that brand's data
  // instead of falling back to Lumina's hardcoded Electrolyte catalog.
  let sku = null;
  let detail = null;
  if (isDemo && profile?.skus?.length) {
    sku = profile.skus.find(s => s.id === skuId) || null;
    // Demo profile also ships per-SKU narrative overrides (positioning,
    // audience, channels, competitors, ingredient highlights, case study).
    // Backend can return them under `sku_details` keyed by sku id.
    detail = profile.sku_details?.[skuId] || profile.sku_details?.[sku?.name] || null;
  }
  // Fallback for non-demo (Lumina / generic) brands → existing hardcoded catalog.
  if (!sku) {
    sku = PORTFOLIO_SKUS.find(s => s.id === skuId);
    detail = SKU_DETAIL[skuId];
  }

  if (!sku) return <Navigate to="/portfolio" replace />;
  const sc = STATUS_COLORS[sku.status] || STATUS_COLORS['Wave-Ready'];

  return (
    <DashboardLayout>
      <div className="p-4 lg:p-6 max-w-[1400px] mx-auto space-y-4" data-testid="product-detail-page">

        <Link to="/portfolio" className="inline-flex items-center gap-2 text-[11px] text-gray-500 hover:text-[#00D4FF] transition-all" data-testid="back-to-portfolio">
          <ArrowLeft className="h-3 w-3" /> Back to Portfolio
        </Link>

        {/* ═══ HERO ═══ */}
        <div className="grid lg:grid-cols-[380px_1fr] gap-4">
          <div className="rounded-2xl overflow-hidden" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
            <img src={sku.image} alt={sku.name} className="w-full h-64 object-cover" data-testid="sku-hero-image" />
          </div>

          <div className="rounded-2xl p-6 flex flex-col justify-between" style={{ background: CARD, border: `1px solid ${BORDER}` }}>
            <div>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-[9px] px-2 py-0.5 rounded-full font-black uppercase tracking-wider"
                      style={{ background: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }}
                      data-testid="sku-status-pill">
                  {sku.status}
                </span>
                <span className="text-[9px] text-gray-600">Last wave: {sku.lastWaveDate}</span>
              </div>
              <h1 className="text-2xl lg:text-3xl font-black text-white leading-tight mb-2" data-testid="sku-name">{sku.name}</h1>
              {detail?.tagline && (
                <p className="text-sm text-gray-300 leading-relaxed mb-4">{detail.tagline}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/build-wave?sku=${encodeURIComponent(sku.name)}`} data-testid="launch-wave-for-sku">
                <button className="px-5 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 transition-all hover:scale-[1.03]"
                        style={{ background: T, color: '#000', boxShadow: `0 0 18px ${T}40` }}>
                  <Zap className="h-3.5 w-3.5" /> Launch Wave for this SKU
                </button>
              </Link>
              <button className="px-4 py-2.5 rounded-xl text-[10px] font-bold flex items-center gap-1.5 transition-all"
                      style={{ background: `${P}15`, color: P, border: `1px solid ${P}30` }}
                      data-testid="rd-test-sku">
                <FlaskConical className="h-3.5 w-3.5" /> Run 25-Creator R&D Test
              </button>
              <Link to="/portfolio" className="px-4 py-2.5 rounded-xl text-[10px] font-bold text-gray-400 hover:text-white transition-all"
                    style={{ background: BG, border: `1px solid ${BORDER}` }}>
                <Calendar className="h-3.5 w-3.5 inline mr-1" /> Wave Calendar
              </Link>
            </div>
          </div>
        </div>

        {/* ═══ STAT RAIL ═══ */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="sku-stat-rail">
          <StatTile label="Revenue"     value={sku.revenue}          color="#4ade80" icon={DollarSign} />
          <StatTile label="ROAS"        value={sku.roas}             color={T}       icon={TrendingUp} />
          <StatTile label="Velocity"    value={sku.velocity === '—' ? '—' : `${sku.velocity}x`} color="#FFB84D" icon={Zap} />
          <StatTile label="Impressions" value={sku.impressions}      color={P}       icon={Eye} />
          <StatTile label="Success"     value={`${sku.successRate}%`} color={T}      icon={Target} />
        </div>

        {/* ═══ AI INSIGHT ═══ */}
        <div className="rounded-xl p-4 flex items-start gap-3"
             style={{ background: `linear-gradient(90deg, ${T}10, ${P}08)`, border: `1px solid ${T}30` }}>
          <Sparkles className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: T }} />
          <div>
            <p className="text-[9px] uppercase tracking-widest font-bold mb-1" style={{ color: T }}>Wave AI Insight</p>
            <p className="text-sm text-white leading-relaxed">{sku.aiInsight}</p>
          </div>
        </div>

        {detail && (
          <>
            {/* ═══ POSITIONING + AUDIENCE ═══ */}
            <div className="grid lg:grid-cols-2 gap-4">
              <Section title="Positioning" icon={Package}>
                <p className="text-sm text-gray-300 leading-relaxed">{detail.positioning}</p>
              </Section>
              <Section title="Target Audience" icon={Users} color={P}>
                <div className="flex flex-wrap gap-2">
                  {detail.target_audience.map(a => (
                    <span key={a} className="px-2.5 py-1 rounded-full text-[10px] font-bold"
                          style={{ background: `${P}15`, color: P, border: `1px solid ${P}30` }}>{a}</span>
                  ))}
                </div>
              </Section>
            </div>

            {/* ═══ CHANNELS + COMPETITORS ═══ */}
            <div className="grid lg:grid-cols-2 gap-4">
              <Section title="Top Channels" icon={BarChart3}>
                <div className="space-y-1.5">
                  {detail.top_channels.map(ch => (
                    <div key={ch} className="flex items-center gap-2 text-[11px] text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: T }} />{ch}
                    </div>
                  ))}
                </div>
              </Section>
              <Section title="Competitor Set" icon={Target} color="#FFB020">
                <div className="flex flex-wrap gap-2">
                  {detail.competitor_set.map(c => (
                    <span key={c} className="px-2.5 py-1 rounded-full text-[10px] font-semibold text-gray-300"
                          style={{ background: BG, border: `1px solid ${BORDER}` }}>{c}</span>
                  ))}
                </div>
              </Section>
            </div>

            {/* ═══ INGREDIENTS ═══ */}
            <Section title="Ingredient Highlights" icon={FlaskConical} color="#4ade80">
              <div className="flex flex-wrap gap-2">
                {detail.ingredient_highlights.map(h => (
                  <span key={h} className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold flex items-center gap-1.5"
                        style={{ background: 'rgba(74,222,128,0.08)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.25)' }}>
                    <Check className="h-3 w-3" /> {h}
                  </span>
                ))}
              </div>
            </Section>

            {/* ═══ CASE STUDY ═══ */}
            {detail.case_study && (
              <Section title="Proven Wave Case Study" icon={Award} color={P}>
                <h3 className="text-lg font-black text-white mb-1.5" data-testid="case-study-headline">{detail.case_study.headline}</h3>
                <p className="text-sm text-gray-300 leading-relaxed mb-4">{detail.case_study.summary}</p>
                <div className="space-y-2">
                  {detail.case_study.proof_points.map((pp, i) => (
                    <div key={`pp-${i}`} className="flex items-start gap-2 rounded-lg p-2.5"
                         style={{ background: BG, border: `1px solid ${BORDER}` }}>
                      <Check className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: T }} />
                      <p className="text-[11px] text-gray-300 leading-relaxed">{pp}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </>
        )}

        {/* ═══ WAVE CALENDAR ═══ */}
        {(sku.calendar || []).length > 0 && (
          <Section title="Wave Calendar" icon={Calendar}>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
              {sku.calendar.map((c) => {
                const statusColor = c.status === 'active' ? '#4ade80' : c.status === 'completed' ? '#888' : T;
                return (
                  <div key={`${c.month}-${c.wave}`} className="rounded-lg p-3 flex items-center gap-3"
                       style={{ background: BG, border: `1px solid ${BORDER}` }} data-testid={`wave-cal-${c.month}`}>
                    <span className="text-[9px] font-black uppercase px-2 py-1 rounded"
                          style={{ background: `${statusColor}15`, color: statusColor }}>
                      {c.month}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-white truncate">{c.wave}</p>
                      <p className="text-[9px] text-gray-500">
                        {c.budget} {c.roas && `· ${c.roas} ROAS`} {c.creators && `· ${c.creators} creators`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </Section>
        )}
      </div>
    </DashboardLayout>
  );
}
